export interface Slot {
  start: string;
  end: string;
}

export interface DateOverride {
  date: string;
  blocked: boolean;
  windowStart?: string;
  windowEnd?: string;
}

export interface AgendaConfig {
  slotDurationMin: number;
  slotIntervalMin: number;
  days: number[];
  windowStart: string;
  windowEnd: string;
  minNoticeMin: number;
  bufferMin: number;
  timezone: string;
  overrides: DateOverride[];
}

export interface UpcomingEvent {
  id: string;
  summary: string;
  start: string;
  end: string;
  htmlLink: string;
}

async function asJson<T>(res: Response): Promise<T> {
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) {
    const err = new Error((data as { error?: string }).error || `HTTP ${res.status}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return data;
}

export async function getSlots(date: string): Promise<Slot[]> {
  const res = await fetch(`/api/slots?date=${date}`);
  const data = await asJson<{ slots: Slot[] }>(res);
  return data.slots;
}

export interface BookInput {
  date: string;
  start: string;
  name: string;
  email: string;
  phone?: string;
  notes?: string;
  lang: string;
  accessToken?: string;
  demand?: any;
  socialLinks?: string[];
  employeeBracket?: string;
  hiredWhat?: string;
  aiTools?: string[];
}

export interface BookResult {
  ok: boolean;
  when: string;
  meetUrl?: string;
  htmlLink?: string;
  emailSent: boolean;
  ghlStatus?: string;
}

export async function book(input: BookInput): Promise<BookResult> {
  const { accessToken, ...body } = input;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }
  const res = await fetch("/api/book", {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  return asJson<BookResult>(res);
}

export interface AdminState {
  config: AgendaConfig;
  connected: boolean;
  email: string;
}

export async function getAdmin(): Promise<AdminState> {
  const res = await fetch("/api/admin/config");
  return asJson<AdminState>(res);
}

export async function saveConfig(config: AgendaConfig): Promise<AgendaConfig> {
  const res = await fetch("/api/admin/config", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(config),
  });
  const data = await asJson<{ config: AgendaConfig }>(res);
  return data.config;
}

export async function getEvents(): Promise<UpcomingEvent[]> {
  const res = await fetch("/api/admin/events");
  const data = await asJson<{ events: UpcomingEvent[] }>(res);
  return data.events;
}
